import { useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";

import { isSafari } from "../../../helpers/browser";
import theme, {
  DEFAULT_FONT_FAMILY,
  PRIMARY_COLOR,
  SECONDARY_COLOR,
  TERTIARY_BLUE_COLOR,
  TERTIARY_GREEN_COLOR,
  TERTIARY_YELLOW_COLOR,
} from "../../../theme";
import { ReactECharts, ReactEChartsProps } from "./ReactECharts";

export type ChartValueOptions = {
  formatter?: (value: string | number) => string;
  tooltipFormatter?: (value: string | number) => string;
};

type ChartDimensionLabels<T> = Record<keyof T, string>;

const buildEChartOption = <TDatum extends Record<string, number | string>>({
  title,
  data,
  dimensions,
  dimensionLabels,
  isMobile = false,
  customColors,
}: {
  title?: string;
  data: TDatum[];
  dimensions: (keyof TDatum)[];
  dimensionLabels: ChartDimensionLabels<TDatum>;
  valueOptions?: ChartValueOptions;
  isMobile?: boolean;
  customColors?: string[];
}): ReactEChartsProps["option"] => {
  const [nameDimension, valueDimension] = dimensions;

  // HACK: We need to add spaces to the legend to ensure the labels are aligned for Safari
  // 1. Find max length
  const maxLen = Math.max(
    0,
    ...data.map((item) => String(item[nameDimension] ?? "").length)
  );
  // 2. Create spaces string
  const spaces = "\u00A0".repeat(maxLen * 1.5);

  const option: ReactEChartsProps["option"] = {
    title: title
      ? {
          text: title,
          left: "center",
          top: "left",
          textStyle: {
            fontWeight: 500,
            fontSize: "1rem",
            color: PRIMARY_COLOR,
          },
        }
      : undefined,
    animation: true,
    animationEasing: "backIn",
    textStyle: {
      fontFamily: DEFAULT_FONT_FAMILY,
      fontWeight: 500,
    },
    tooltip: {
      trigger: "item",
      formatter: (params) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = params as any;

        const percent =
          typeof item.percent === "number" ? `${item.percent}%` : "";

        return `${item.marker}${item.name}: ${percent}`;
      },
    },
    legend: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      selectedMode: false,
      icon: "circle",
      itemWidth: 10,
      itemGap: 15,
      left: "center",
      bottom: 0,
      textStyle: {
        fontSize: "0.688rem",
      },
      formatter: (name) => {
        return `${name}${isSafari() ? spaces : ""}`;
      },
    },
    color: customColors ?? [
      PRIMARY_COLOR,
      SECONDARY_COLOR,
      TERTIARY_YELLOW_COLOR,
      TERTIARY_GREEN_COLOR,
      TERTIARY_BLUE_COLOR,
    ],
    animationDuration: 250,
    dataset: {
      source: data,
    },
    series: [
      {
        type: "pie",
        name: dimensionLabels[valueDimension],
        radius: isMobile ? "55%" : "60%",
        center: ["50%", "48%"],
        encode: {
          itemName: nameDimension as string,
          value: valueDimension as string,
        },
        minShowLabelAngle: 0.01,
        label: {
          fontFamily: DEFAULT_FONT_FAMILY,
          fontSize: "0.688rem",
          formatter: (params) => {
            const percent =
              typeof params.percent === "number" ? `${params.percent}%` : "";
            return percent;
          },
        },

        emphasis: {
          focus: "self",
          blurScope: "coordinateSystem",
        },
      },
    ],
  };

  return option;
};

const buildEmptySource = <
  TDatum extends Record<string, number | string | Date>
>({
  data,
  dimensions,
}: {
  data: TDatum[];
  dimensions: (keyof TDatum)[];
}): TDatum[] => {
  const [, ...valueDimensions] = dimensions;

  return data.map((item) => {
    const newItem = { ...item };

    valueDimensions.forEach((dimension) => {
      if (typeof newItem[dimension] === "number") {
        newItem[dimension] = 0 as TDatum[keyof TDatum];
      }
    });

    return newItem;
  });
};

const ReactEPieChart = <TDatum extends Record<string, number | string>>({
  title,
  data,
  dimensions,
  dimensionLabels,
  valueOptions,
  customColors,
}: {
  title?: string;
  data: TDatum[];
  dimensions: (keyof TDatum)[];
  dimensionLabels: ChartDimensionLabels<TDatum>;
  valueOptions?: ChartValueOptions;
  customColors?: string[];
}) => {
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down("sm"));

  const option = buildEChartOption({
    title,
    data,
    dimensions,
    dimensionLabels,
    valueOptions,
    isMobile: mobileBreakpoint,
    customColors,
  });
  const emptySource = buildEmptySource({ data, dimensions });

  const [eChartData, setEChartData] = useState({
    ...option,
    animation: false,
    dataset: {
      ...option.dataset,
      source: emptySource,
    },
  } as ReactEChartsProps["option"]);

  useEffect(() => {
    setTimeout(() => setEChartData(option), 0);
  }, []);

  return <ReactECharts option={eChartData} />;
};

export default ReactEPieChart;
